import { Clock, Megaphone, Rocket, Sparkles, Target, Trash2 } from 'lucide-react';
import { useSettings } from '../settings';
import { formatGenerationDurationMs, getGenerationDurationMs } from '../utils/generationTiming';
import { confirmDialog } from './ConfirmDialog';

const STARTERS = [
  {
    title: 'Product launch',
    description: 'Position a new offer and create a premium launch campaign.',
    icon: Rocket,
    values: {
      campaign_goal: 'Launch a new product',
      generation_strategy: 'AIDA',
      desired_visual_style: 'Cinematic launch ad with dramatic product spotlight',
      poster_style: 'cinematic_launch',
    },
  },
  {
    title: 'Lead generation',
    description: 'Build a clear, trust-led campaign around one next step.',
    icon: Target,
    values: {
      campaign_goal: 'Generate leads',
      generation_strategy: 'Trust-Focused',
      tone: 'Professional and trustworthy',
      desired_visual_style: 'Professional B2B trust-building visual',
      poster_style: 'tech_editorial',
    },
  },
  {
    title: 'Limited offer',
    description: 'Turn a time-sensitive promotion into channel-ready creative.',
    icon: Megaphone,
    values: {
      campaign_goal: 'Promote a discount campaign',
      generation_strategy: 'PAS',
      desired_visual_style: 'Bold high-contrast product hero ad',
      poster_style: 'bold_fmcg',
    },
  },
];

function SavedCampaigns({
  campaigns,
  onSelect,
  onUseTemplate,
  onDelete,
  deletingCampaignId = '',
}) {
  const { t } = useSettings();

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    e.currentTarget.style.setProperty('--mouse-x', `${x}px`);
    e.currentTarget.style.setProperty('--mouse-y', `${y}px`);
  };

  const handleDelete = async (campaign) => {
    const confirmed = await confirmDialog({
      title: 'Delete this saved campaign?',
      message: 'This action may permanently remove the campaign and its associated generated media. It cannot be undone.',
      confirmLabel: 'Delete campaign',
      cancelLabel: 'Keep campaign',
      danger: true,
    });
    if (confirmed) {
      await onDelete?.(campaign);
    }
  };

  return (
    <section className="panel saved-panel glow-card" onMouseMove={handleMouseMove}>
      <div className="section-heading compact-heading">
        <p className="eyebrow">{t('saved.eyebrow')}</p>
        <h2>{t('saved.title')}</h2>
        <p>{t('saved.description')}</p>
      </div>

      <div className="saved-list" role="region" aria-label={t('saved.title')} tabIndex={0}>
        {campaigns.length === 0 ? (
          <div className="saved-starters">
            <div className="saved-starters-head">
              <span><Sparkles size={15} /></span>
              <div>
                <strong>Start with a campaign goal</strong>
                <p>Load a polished direction, then make the brief yours.</p>
              </div>
            </div>
            <div className="saved-starter-list">
              {STARTERS.map(({ title, description, icon: Icon, values }) => (
                <button type="button" className="saved-starter" key={title} onClick={() => onUseTemplate?.(values)}>
                  <i><Icon size={15} /></i>
                  <span><strong>{title}</strong><small>{description}</small></span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          campaigns.map((campaign) => {
            const deleting = deletingCampaignId === campaign.id;
            const generationDurationMs = getGenerationDurationMs(campaign);
            return (
              <div className="saved-item" key={campaign.id}>
                <button
                  type="button"
                  className="saved-item-open"
                  onClick={() => onSelect?.(campaign)}
                  title="Reopen this campaign"
                >
                  <div>
                    <h3>{campaign.campaign_title}</h3>
                    <p>{campaign.input?.brand_name} · {campaign.input?.product_name}</p>
                  </div>
                  <span>
                    <Clock size={14} />
                    {new Date(campaign.created_at).toLocaleDateString()}
                    {generationDurationMs != null && ` · ${formatGenerationDurationMs(generationDurationMs)} generation`}
                  </span>
                </button>
                <button
                  type="button"
                  className="saved-item-delete"
                  aria-label={`Delete ${campaign.campaign_title || 'saved campaign'}`}
                  title="Delete saved campaign"
                  disabled={deleting}
                  onClick={() => handleDelete(campaign)}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

export default SavedCampaigns;
